/**
 * delivery-service.js
 *
 * Adds/removes one of two hidden delivery-service products — "4-Man Delivery
 * Service" and "Standard delivery (without 4-hand service)" — based on checkboxes shown on the
 * cart page and in the cart drawer whenever cart.total_weight exceeds a
 * merchant-configured threshold (in kg, stored per-service as
 * thresholdGrams).
 *
 * MUTUAL EXCLUSIVITY
 * --------------------------------------------------------------------------
 * Only one of the two services can be active at a time. As long as one
 * service's product is present in the cart, the OTHER service's checkbox is
 * not rendered at all (so it simply can't be selected) — regardless of
 * whether its own weight threshold is met. As soon as the selected service
 * is removed from the cart (its own checkbox unchecked, or the line item
 * removed from the cart/cart drawer), both services immediately become
 * selectable again, each following its own normal weight-threshold rule.
 *
 * CORE DESIGN — no delays, no DOM-mutation guessing
 * --------------------------------------------------------------------------
 * Every Shopify Ajax Cart endpoint that changes the cart —
 * /cart/add.js, /cart/change.js, /cart/update.js, /cart/select.js,
 * /cart/clear.js — returns the fresh, authoritative cart JSON (including
 * total_weight) the instant the mutation finishes, no matter which script on
 * the page made the call. So instead of waiting an arbitrary delay or
 * watching the DOM for changes and hoping we caught the right moment, this
 * file intercepts those exact responses (via both `fetch` and
 * `XMLHttpRequest`, since we don't know which one any given add-to-cart
 * implementation uses) and re-renders every service's checkbox straight from
 * that data. This guarantees the weight/exclusivity check always happens
 * strictly AFTER the cart item has actually been added/removed/changed —
 * never before, never on a guess — regardless of whether the change came
 * from a collection page product-card, a product page buy-button, the cart
 * page, or the drawer.
 *
 * REMOVING A SERVICE ITEM SAFELY
 * --------------------------------------------------------------------------
 * A service line item is always removed by its cart line item `key` (fetched
 * live right before acting), never by array position ("line") and never by
 * bare variant ID alone — both are looser matches that can silently target
 * the wrong line item if another cart change happens around the same time.
 * An in-flight guard also stops overlapping add/remove requests for the same
 * checkbox's own manual click.
 *
 * CONFIG SHAPE
 * --------------------------------------------------------------------------
 * Each rendering section (cart-drawer.liquid, main-cart-items.liquid) prints
 * a single <script type="application/json" data-delivery-service-config">
 * tag shaped like:
 *   {
 *     "services": [
 *       { "key": "service_1", "variantId": 123, "thresholdGrams": 25000,
 *         "label": "Add Four-hand service",
 *         "description": "Service descriptions",
 *         "containers": ["DeliveryServiceContainer", "DeliveryServiceContainerDrawer"] },
 *       { "key": "service_2", "variantId": 456, "thresholdGrams": 25000,
 *         "label": "Add Standard delivery (without 4-hand service)",
 *         "description": "Service descriptions",
 *         "containers": ["DeliveryServiceContainer2", "DeliveryServiceContainerDrawer2"] }
 *     ]
 *   }
 * `containers` are the element IDs (no leading #) that should be kept in
 * sync for that service — the cart page container and the cart drawer
 * container both live under the same service entry.
 */

(function () {
  if (window.__deliveryServiceInit) return;
  window.__deliveryServiceInit = true;

  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const CART_MUTATION_PATTERN = /\/cart\/(add|change|update|select|clear)(\.js)?(\?|$|\/)/;

  const cachedTemplateHTML = Object.create(null); // keyed by service.key
  let checkboxActionInFlight = false; // guards only a checkbox's own manual click
  const lastAutoRemovalAttempt = Object.create(null); // keyed by service.key, lightweight debounce

  // The cart drawer and the cart page each print their own
  // [data-delivery-service-config] tag (they're separate sections with
  // separate settings). Both are normally present on the actual cart page at
  // once. Rather than trusting only the first tag found — which would let an
  // incompletely-configured section silently blank out a service that IS
  // configured correctly elsewhere on the page — merge every tag's services
  // by key, keeping the first non-blank entry seen for each key.
  function getConfig() {
    const els = document.querySelectorAll('[data-delivery-service-config]');
    if (!els.length) return null;

    const merged = new Map();

    els.forEach((el) => {
      let parsed;
      try {
        parsed = JSON.parse(el.textContent);
      } catch (e) {
        return;
      }

      if (parsed && Array.isArray(parsed.services)) {
        parsed.services.forEach((service) => {
          if (service && service.key && service.variantId != null && !merged.has(service.key)) {
            merged.set(service.key, service);
          }
        });
      } else if (parsed && parsed.variantId != null && !merged.has('service_1')) {
        // Backwards compatibility with the old single-service config shape.
        merged.set('service_1', {
          key: 'service_1',
          variantId: parsed.variantId,
          thresholdGrams: parsed.thresholdGrams,
          label: 'Add delivery service',
          containers: ['DeliveryServiceContainer', 'DeliveryServiceContainerDrawer'],
        });
      }
    });

    if (!merged.size) return null;
    return { services: Array.from(merged.values()) };
  }

  function findServiceIndex(cartData, variantId) {
    if (!cartData || !Array.isArray(cartData.items)) return -1;
    return cartData.items.findIndex((item) => String(item.variant_id) === String(variantId));
  }

  function hasPubSub() {
    return typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';
  }

  // --- Sends a single /cart/change.js request with the given payload, ---
  // --- always including the sections cartBase itself would refresh.   ---
  function performCartChange(payload) {
    const sections = window.cartBase ? window.cartBase.getSectionsToRender().map((s) => s.id) : [];

    return fetch(`${ROOT}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(
        Object.assign({}, payload, {
          sections,
          sections_url: window.location.pathname,
        })
      ),
      cache: 'no-store',
    }).then((res) => res.json());
  }

  function applyCartResponse(response, onLoadingToggle) {
    if (window.cartBase && response && !response.errors) {
      window.cartBase.cartData = response;
      if (hasPubSub()) {
        window.cartBase.buildEvent(PUB_SUB_EVENTS.quantityUpdate);
        window.cartBase.publishEventFinish = () => {
          window.cartBase.buildEvent(PUB_SUB_EVENTS.quantityUpdateFinish);
        };
      }
      window.cartBase.updateSections(response, onLoadingToggle);
    } else if (onLoadingToggle) {
      onLoadingToggle(false);
    }
  }

  // --- Removes a service item robustly: ---
  //   1. Fetches the LIVE cart right before acting (never trusts a possibly
  //      stale snapshot), so we know exactly which line item we're
  //      targeting right now.
  //   2. Identifies it by its cart line item `key` rather than the bare
  //      variant ID, which is a looser match.
  //   3. Falls back to position-based removal only if key-based removal is
  //      explicitly rejected by the server.
  function removeServiceItem(variantId, onLoadingToggle) {
    if (onLoadingToggle) onLoadingToggle(true);

    return fetch(`${ROOT}cart.js`, { cache: 'no-store' })
      .then((res) => res.json())
      .then((liveCart) => {
        const idx = findServiceIndex(liveCart, variantId);

        if (idx === -1) {
          // Already gone — nothing to do.
          if (onLoadingToggle) onLoadingToggle(false);
          return null;
        }

        const item = liveCart.items[idx];
        const identifier = item.key || variantId;

        return performCartChange({ id: identifier, quantity: 0 }).then((response) => {
          if (response && response.errors) {
            console.warn(
              '[delivery-service] key-based removal was rejected, retrying by line position:',
              response.errors
            );
            return performCartChange({ line: idx + 1, quantity: 0 });
          }
          return response;
        });
      })
      .then((response) => {
        if (response && response.errors) {
          console.error('[delivery-service] failed to remove the service item:', response.errors);
        }
        applyCartResponse(response, onLoadingToggle);
        return response;
      })
      .catch((error) => {
        console.error('[delivery-service] remove failed:', error);
        if (onLoadingToggle) onLoadingToggle(false);
        return null;
      });
  }

  // --- Template capture, per service, so a dynamically-rebuilt checkbox ---
  // --- still shows the merchant's real configured label.                ---
  function cacheTemplateFrom(serviceKey, checkboxEl) {
    if (!checkboxEl) return;
    const clone = checkboxEl.cloneNode(true);
    const input = clone.querySelector('[data-delivery-service-input]');
    if (input) input.removeAttribute('checked');
    cachedTemplateHTML[serviceKey] = clone.outerHTML;
    try {
      localStorage.setItem('deliveryServiceTemplateHTML_' + serviceKey, cachedTemplateHTML[serviceKey]);
    } catch (e) {
      /* localStorage unavailable — non-fatal */
    }
  }

   // Builds the checkbox markup from service config. Supports an optional
  // "description" (second line of text) under the title. This is purely
  // additive — a service config with no description set renders exactly as
  // it always has (title-only).
  function defaultTemplateHTML(service, containerId) {
    const isDrawer = containerId.indexOf('Drawer') !== -1;
    const suffix = service.key === 'service_2' ? '2' : '';
    const inputId = (isDrawer ? 'CartDrawer-DeliveryServiceInput' : 'DeliveryServiceInput') + suffix;
    const extraClass = isDrawer ? ' cart-drawer__delivery-service' : '';
    const title = service.title || service.label || 'Add delivery service';
    const description = service.description || '';
    const showMoreLabel = service.showMoreLabel || 'Show more';
    const showLessLabel = service.showLessLabel || 'Show less';

    return (
        '<delivery-service-checkbox class="delivery-service-checkbox' +
        extraClass +
        '" data-variant-id="' +
        service.variantId +
        '" data-service-key="' +
        service.key +
        '">' +
        '<label class="delivery-service-checkbox__label" for="' +
        inputId +
        '">' +
        '<input type="checkbox" id="' +
        inputId +
        '" class="delivery-service-checkbox__input" data-delivery-service-input>' +
        '<span class="delivery-service-checkbox__content">' +
        '<span class="delivery-service-checkbox__title">' +
        title +
        '</span>' +
        (description
        ? '<span class="delivery-service-checkbox__description-wrap" data-description-wrap>' +
            '<span class="delivery-service-checkbox__description" data-description-text>' + description + '</span>' +
            '</span>' +
            '<button type="button" class="delivery-service-checkbox__toggle" data-description-toggle ' +
            'data-show-more-label="' + showMoreLabel + '" data-show-less-label="' + showLessLabel + '" ' +
            'aria-expanded="false" hidden>' +
            '<span data-toggle-label>' + showMoreLabel + '</span>' +
            '<svg class="delivery-service-checkbox__toggle-icon" viewBox="0 0 12 8" aria-hidden="true">' +
            '<path d="M1 1l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
            '</button>'
        : '') +
        '</span>' +
        '</label>' +
        '<div class="delivery-service-checkbox__spinner hidden" data-delivery-service-spinner>' +
        '<svg aria-hidden="true" focusable="false" role="presentation" class="icon-loading-spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
        '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>' +
        '</svg>' +
        '</div>' +
        '</delivery-service-checkbox>'
    );
  }

  function getTemplateHTML(service, containerId) {
    if (cachedTemplateHTML[service.key]) return cachedTemplateHTML[service.key];
    try {
      const stored = localStorage.getItem('deliveryServiceTemplateHTML_' + service.key);
      if (stored) {
        cachedTemplateHTML[service.key] = stored;
        return stored;
      }
    } catch (e) {
      /* ignore */
    }
    return defaultTemplateHTML(service, containerId);
  }

  // Is any OTHER configured service currently present as a cart line item?
  // While true, this service's checkbox stays hidden — regardless of its own
  // weight threshold — so it can't be selected. Once the sibling is removed
  // this returns false again and normal threshold-based visibility resumes
  // for both services.
  function isAnotherServiceSelected(cartData, services, currentKey) {
    return services.some((service) => {
      if (service.key === currentKey) return false;
      return findServiceIndex(cartData, service.variantId) !== -1;
    });
  }

  // --- Core sync: render one container purely from live cart data. ---
  function syncContainer(containerId, cartData, service, services) {
    const container = document.getElementById(containerId);
    if (!container) return; // this container isn't on the current page

    const blockedByExclusivity = isAnotherServiceSelected(cartData, services, service.key);
    const overThreshold = cartData.total_weight > service.thresholdGrams;
    const shouldShow = overThreshold && !blockedByExclusivity;

    const serviceIndex = findServiceIndex(cartData, service.variantId);
    const checked = serviceIndex !== -1;
    const existingCheckbox = container.querySelector('delivery-service-checkbox');
    const existingInput = existingCheckbox && existingCheckbox.querySelector('[data-delivery-service-input]');
    const alreadyCorrect =
      (shouldShow && existingInput && existingInput.checked === checked) ||
      (!shouldShow && !existingCheckbox);

    if (alreadyCorrect) {
      if (existingCheckbox) cacheTemplateFrom(service.key, existingCheckbox);
      return;
    }

    if (shouldShow) {
      if (existingCheckbox) {
        cacheTemplateFrom(service.key, existingCheckbox);
        if (existingInput && document.activeElement !== existingInput) existingInput.checked = checked;
      } else {
        container.innerHTML = getTemplateHTML(service, containerId);
        const freshInput = container.querySelector('[data-delivery-service-input]');
        if (freshInput) freshInput.checked = checked;
      }
    } else if (existingCheckbox) {
      container.innerHTML = '';
    }
  }

  function removeServiceIfUnderThreshold(cartData, service) {
    const serviceIndex = findServiceIndex(cartData, service.variantId);
    const overThreshold = cartData.total_weight > service.thresholdGrams;

    if (overThreshold || serviceIndex === -1) return;

    // Lightweight debounce only — never a permanent lock. The next
    // syncAllContainers() call (there will always be one) tries again
    // automatically once the debounce window has passed.
    const now = Date.now();
    if (now - (lastAutoRemovalAttempt[service.key] || 0) < 800) return;
    lastAutoRemovalAttempt[service.key] = now;

    removeServiceItem(service.variantId, () => {});
  }

  // The single entry point every trigger below funnels into: given a fresh,
  // authoritative cart JSON, make every service's UI and line item match it.
  function syncAllContainers(cartData) {
    const config = getConfig();
    if (!config || !cartData || typeof cartData.total_weight !== 'number') return;

    config.services.forEach((service) => {
      (service.containers || []).forEach((containerId) =>
        syncContainer(containerId, cartData, service, config.services)
      );
      removeServiceIfUnderThreshold(cartData, service);
    });
  }

  // Shopify's /cart/add(.js) response only contains the newly-added line
  // item's own data — it does NOT include total_weight or the full items
  // array the way /cart/change.js, /cart/update.js, and /cart.js do. So any
  // intercepted response that isn't already a full cart object gets a
  // follow-up GET /cart.js to obtain the real, authoritative cart state
  // before we try to sync from it.
  function handleMutationResponseData(data) {
    if (data && typeof data.total_weight === 'number') {
      syncAllContainers(data);
      return;
    }

    fetch(`${ROOT}cart.js`, { cache: 'no-store' })
      .then((res) => res.json())
      .then((cart) => syncAllContainers(cart))
      .catch(() => {});
  }

  // --- Primary trigger: intercept the actual response of every cart ---
  // --- mutation endpoint, from ANY calling code, via fetch AND XHR.   ---
  function installFetchInterceptor() {
    const originalFetch = window.fetch;
    window.fetch = function (...args) {
      const input = args[0];
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const isCartMutation = CART_MUTATION_PATTERN.test(url);

      return originalFetch.apply(this, args).then((response) => {
        if (isCartMutation && response.ok) {
          response
            .clone()
            .json()
            .then((data) => handleMutationResponseData(data))
            .catch(() => {});
        }
        return response;
      });
    };
  }

  function installXhrInterceptor() {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__deliveryServiceIsCartMutation = CART_MUTATION_PATTERN.test(String(url));
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      if (this.__deliveryServiceIsCartMutation) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) {
            try {
              handleMutationResponseData(JSON.parse(this.responseText));
            } catch (e) {
              /* not JSON, or unrelated response shape — ignore */
            }
          }
        });
      }
      return originalSend.apply(this, args);
    };
  }

  // --- Secondary/redundant trigger: cartBase's own pub/sub events, when ---
  // --- available. Harmless if it never fires, since the interceptors    ---
  // --- above already cover the same moment via the raw network response.---
  function watchPubSub() {
    if (!hasPubSub()) return;
    [PUB_SUB_EVENTS.quantityUpdateFinish, PUB_SUB_EVENTS.cartUpdateFinish].forEach((eventName) => {
      if (!eventName) return;
      subscribe(eventName, (event) => {
        if (event && event.cartData) syncAllContainers(event.cartData);
      });
    });
  }

  function verifyAgainstLiveCart(delay) {
    setTimeout(() => {
      fetch(`${ROOT}cart.js`, { cache: 'no-store' })
        .then((res) => res.json())
        .then((cart) => syncAllContainers(cart))
        .catch(() => {});
    }, delay);
  }

  // --- Third/redundant trigger: listen directly to the exact DOM events ---
  // --- the theme's own quantity +/- buttons and remove buttons produce.  ---
  function watchQuantityAndRemoveControls() {
    document.addEventListener(
      'change',
      (event) => {
        if (event.target && event.target.classList && event.target.classList.contains('quantity__input')) {
          verifyAgainstLiveCart(700);
        }
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('cart-remove-button')) {
          verifyAgainstLiveCart(700);
        }
      },
      true
    );
  }

  function init() {
    installFetchInterceptor();
    installXhrInterceptor();
    watchPubSub();
    watchQuantityAndRemoveControls();

    // One check on page load, in case the cart is already in an
    // inconsistent state (e.g. threshold lowered by a merchant since a
    // service was added, or two services somehow both present).
    verifyAgainstLiveCart(0);
  }

  // --- The checkbox custom element ---
  class DeliveryServiceCheckbox extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('[data-delivery-service-input]');
      this.spinner = this.querySelector('[data-delivery-service-spinner]');
      this.variantId = this.dataset.variantId;

      if (!this.checkbox || !this.variantId) return;

      this._onChange = this.onChange.bind(this);
      this.checkbox.addEventListener('change', this._onChange);

      this.initDescriptionToggle();
    }

    disconnectedCallback() {
        if (this.checkbox) this.checkbox.removeEventListener('change', this._onChange);
        if (this._onToggleClick && this.toggleButton) {
            this.toggleButton.removeEventListener('click', this._onToggleClick);
        }
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
    }
    initDescriptionToggle() {
        this.descriptionWrap = this.querySelector('[data-description-wrap], [data-product-description-wrap]');
        this.toggleButton = this.querySelector('[data-description-toggle], [data-product-description-toggle]');
        if (!this.descriptionWrap || !this.toggleButton) return;

        this.showMoreLabel = this.toggleButton.dataset.showMoreLabel || 'Show more';
        this.showLessLabel = this.toggleButton.dataset.showLessLabel || 'Show less';

        this._onToggleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleDescription();
        };
        this.toggleButton.addEventListener('click', this._onToggleClick);

        this._onResize = debounce(() => this.updateToggleVisibility(), 150);
        window.addEventListener('resize', this._onResize);

        requestAnimationFrame(() => this.updateToggleVisibility());

        if ('IntersectionObserver' in window) {
            this._observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.updateToggleVisibility();
                    }
                });
            }, { root: null, threshold: 0 });
            this._observer.observe(this);
        } else {
            setTimeout(() => this.updateToggleVisibility(), 250);
        }
    }

    updateToggleVisibility() {
        if (!this.descriptionWrap || !this.toggleButton) return;
        if (this.descriptionWrap.classList.contains('is-expanded')) return;

        const collapsedMax = this.descriptionWrap.style.maxHeight;
        this.descriptionWrap.style.maxHeight = 'none';
        const fullHeight = this.descriptionWrap.scrollHeight;
        this.descriptionWrap.style.maxHeight = collapsedMax;

        const collapsedHeight = this.descriptionWrap.clientHeight;
        this.toggleButton.hidden = !(fullHeight > collapsedHeight + 1);
    }

    toggleDescription() {
        const expanded = this.descriptionWrap.classList.toggle('is-expanded');

        if (expanded) {
        const fullHeight = this.descriptionWrap.scrollHeight;
        this.descriptionWrap.style.setProperty('--description-expanded-height', fullHeight + 'px');
        }

        this.toggleButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        const label = this.toggleButton.querySelector('[data-toggle-label]');
        if (label) label.textContent = expanded ? this.showLessLabel : this.showMoreLabel;
    }

    setLoading(isLoading) {
      if (this.checkbox) this.checkbox.disabled = isLoading;
      this.classList.toggle('delivery-service-checkbox--loading', isLoading);
      if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
    }

    onChange(event) {
      const checked = event.target.checked;

      if (checkboxActionInFlight) {
        // Another add/remove (from either service's checkbox) is already in
        // progress — revert this click rather than risk an overlapping
        // request that could leave both services active at once.
        event.target.checked = !checked;
        return;
      }

      this.setLoading(true);

      if (!window.cartBase) {
        console.error('[delivery-service] cartBase is not available yet — try again in a moment.');
        event.target.checked = !checked;
        this.setLoading(false);
        return;
      }

      checkboxActionInFlight = true;
      // Safety valve: never let this flag get stuck forever even if some
      // callback along the way never fires for an unexpected reason.
      const safetyTimeout = setTimeout(() => {
        checkboxActionInFlight = false;
      }, 15000);
      const clearFlag = () => {
        clearTimeout(safetyTimeout);
        checkboxActionInFlight = false;
      };

      if (checked) {
        const body = new FormData();
        body.append('id', this.variantId);
        body.append('quantity', 1);

        window.cartBase.addToCart({
          body,
          source: 'delivery-service',
          target: {
            onError: (errors) => {
              if (errors && errors.length) console.warn('[delivery-service]', errors);
            },
          },
          onLoadingToggle: (isLoading) => {
            this.setLoading(isLoading);
            if (!isLoading) clearFlag();
          },
        });
      } else {
        removeServiceItem(this.variantId, (isLoading) => {
          this.setLoading(isLoading);
          if (!isLoading) clearFlag();
        });
      }
    }
  }

  if (!customElements.get('delivery-service-checkbox')) {
    customElements.define('delivery-service-checkbox', DeliveryServiceCheckbox);
  }

  // Cache whatever the server rendered initially, per service, so a later
  // dynamic rebuild (on a page where a container starts empty) still uses
  // the real configured label.
  document.querySelectorAll('delivery-service-checkbox').forEach((el) => {
    const key = el.dataset.serviceKey || 'service_1';
    cacheTemplateFrom(key, el);
  });

  if (window.scriptController && typeof window.scriptController.executeWhenScriptLoaded === 'function') {
    window.scriptController.executeWhenScriptLoaded('cart.js', init);
  } else {
    const interval = setInterval(() => {
      if (window.cartBase) {
        clearInterval(interval);
        init();
      }
    }, 100);
    setTimeout(() => {
      clearInterval(interval);
      init(); // still install interceptors even if cartBase never shows up
    }, 5000);
  }
})();

//  checkout weight lock

/**
 * checkout-weight-lock.js
 *
 * ADD-ON script — does not modify or remove anything from delivery-service.js
 * or the cart-drawer / main-cart-items liquid sections. It is fully
 * self-contained and simply reads the SAME [data-delivery-service-config]
 * tags those sections already print.
 *
 * BEHAVIOR
 * --------------------------------------------------------------------------
 * - As soon as cart.total_weight exceeds EITHER configured service's
 *   thresholdGrams, the checkout button (.cart-drawer__ctas button on the
 *   cart page, and the theme's #checkout / button[name="checkout"]) is
 *   disabled, and any "dynamic checkout buttons" wrapper (Shop Pay / PayPal
 *   express buttons etc.) is locked out too — in BOTH the cart drawer and
 *   the cart page.
 * - The moment ONE of the two delivery-service line items is present in the
 *   cart (i.e. the customer checked a delivery-service checkbox), the lock
 *   is lifted again and checkout works normally — even if still over
 *   threshold.
 * - If the cart drops back under both thresholds, the lock is lifted
 *   regardless of whether a service is selected.
 *
 * Assumption: "over threshold" = over the threshold of AT LEAST ONE of the
 * two configured services (service_1 OR service_2), since either one alone
 * is enough to trigger its own checkbox being shown.
 *
 * Uses the exact same "intercept every Shopify cart-mutation endpoint's
 * response" approach as delivery-service.js, so the lock/unlock always
 * happens strictly AFTER a cart change has actually completed — never on a
 * guess, and never dependent on delivery-service.js having run first.
 */

(function () {
  if (window.__checkoutWeightLockInit) return;
  window.__checkoutWeightLockInit = true;

  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const CART_MUTATION_PATTERN = /\/cart\/(add|change|update|select|clear)(\.js)?(\?|$|\/)/;

  // Adjust/extend these if your theme uses different markup for the cart
  // page's checkout button or express-checkout wrapper — the drawer
  // selectors already match cart-drawer.liquid as given.
  const CHECKOUT_BUTTON_SELECTORS = [
    '.cart-drawer__ctas button',
    '#checkout',
    'button[name="checkout"]',
  ];
  const DYNAMIC_CHECKOUT_SELECTORS = [
    '.cart-drawer__dynamic-checkout-buttons',
    '.cart__dynamic-checkout-buttons',
    '.additional-checkout-buttons',
  ];
  const LOCK_CLASS = 'checkout-weight-lock--disabled';

  // Reads/merges the same config tag(s) delivery-service.js reads, so the
  // two files always agree on thresholds/variant IDs without being coupled.
  function getServices() {
    const els = document.querySelectorAll('[data-delivery-service-config]');
    if (!els.length) return null;

    const merged = new Map();
    els.forEach((el) => {
      let parsed;
      try {
        parsed = JSON.parse(el.textContent);
      } catch (e) {
        return;
      }
      if (parsed && Array.isArray(parsed.services)) {
        parsed.services.forEach((service) => {
          if (service && service.key && service.variantId != null && !merged.has(service.key)) {
            merged.set(service.key, service);
          }
        });
      }
    });

    return merged.size ? Array.from(merged.values()) : null;
  }

  function isAnyServiceInCart(cartData, services) {
    if (!cartData || !Array.isArray(cartData.items)) return false;
    return services.some((service) =>
      cartData.items.some((item) => String(item.variant_id) === String(service.variantId))
    );
  }

  function isOverAnyThreshold(cartData, services) {
    return services.some(
      (service) => typeof service.thresholdGrams === 'number' && cartData.total_weight > service.thresholdGrams
    );
  }

  function queryAllUnique(selectors) {
    const found = [];
    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        if (found.indexOf(el) === -1) found.push(el);
      });
    });
    return found;
  }

  function setLocked(isLocked) {
    queryAllUnique(CHECKOUT_BUTTON_SELECTORS).forEach((btn) => {
      btn.disabled = isLocked;
      btn.classList.toggle(LOCK_CLASS, isLocked);
      btn.setAttribute('aria-disabled', isLocked ? 'true' : 'false');
    });

    queryAllUnique(DYNAMIC_CHECKOUT_SELECTORS).forEach((wrap) => {
      wrap.classList.toggle(LOCK_CLASS, isLocked);
      wrap.setAttribute('aria-hidden', isLocked ? 'true' : 'false');
    });
  }

    // Add near the other selector constants:
    const NOTICE_SELECTORS = ['#DeliveryServiceNotice', '#DeliveryServiceNoticeDrawer'];

    // Add this new function near setLocked():
    function setNoticeVisibility(showNotice) {
        queryAllUnique(NOTICE_SELECTORS).forEach((el) => {
            el.classList.toggle('hidden', !showNotice);
            el.setAttribute('aria-hidden', showNotice ? 'false' : 'true');
        });
    }

    function evaluate(cartData) {
    const services = getServices();

    if (!services || !cartData || typeof cartData.total_weight !== 'number') {
        setLocked(false);
        setNoticeVisibility(false);
        return;
    }

    const overThreshold = isOverAnyThreshold(cartData, services);
    const anySelected = isAnyServiceInCart(cartData, services);
    const shouldBlock = overThreshold && !anySelected;

    setLocked(shouldBlock);
    setNoticeVisibility(shouldBlock);
    }

  function fetchLiveCartAndEvaluate() {
    fetch(`${ROOT}cart.js`, { cache: 'no-store' })
      .then((res) => res.json())
      .then((cart) => evaluate(cart))
      .catch(() => {});
  }

  // /cart/add.js responses don't include total_weight — fall back to a
  // live GET /cart.js, same as delivery-service.js does.
  function handleMutationResponseData(data) {
    if (data && typeof data.total_weight === 'number') {
      evaluate(data);
      return;
    }
    fetchLiveCartAndEvaluate();
  }

  function installFetchInterceptor() {
    const originalFetch = window.fetch;
    window.fetch = function (...args) {
      const input = args[0];
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const isCartMutation = CART_MUTATION_PATTERN.test(url);

      return originalFetch.apply(this, args).then((response) => {
        if (isCartMutation && response.ok) {
          response
            .clone()
            .json()
            .then((data) => handleMutationResponseData(data))
            .catch(() => {});
        }
        return response;
      });
    };
  }

  function installXhrInterceptor() {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__checkoutWeightLockIsCartMutation = CART_MUTATION_PATTERN.test(String(url));
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      if (this.__checkoutWeightLockIsCartMutation) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) {
            try {
              handleMutationResponseData(JSON.parse(this.responseText));
            } catch (e) {
              /* not JSON, or unrelated response shape — ignore */
            }
          }
        });
      }
      return originalSend.apply(this, args);
    };
  }

  function init() {
    installFetchInterceptor();
    installXhrInterceptor();
    // Evaluate once on load, in case the page already loaded in an
    // over-threshold state (e.g. after a full page refresh on /cart).
    fetchLiveCartAndEvaluate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();